import { supabase, publicSupabase } from "./supabaseClient";

export interface IArticle {
  id: number;
  title: string;
  content: string;
  author_id: string;
  category: "Technology" | "Politics" | "Culture" | "Science";
  imageUrl?: string;
  status: "draft" | "published" | "archived";
  created_at: string;
  slug?: string;
  excerpt?: string;
  reading_time?: number;
  views: number;
  // From joined profile data
  author_username?: string;
  author_bio?: string;
  author_avatar?: string;
  author_first_name?: string;
  author_last_name?: string;
  firstName?: string;
  lastName?: string;
}

// Interface for creating a new article
export type CreateArticleDTO = {
  title: string;
  content: string;
  category: "Technology" | "Politics" | "Culture" | "Science";
  imageUrl?: string;
  status?: "draft" | "published";
  excerpt?: string;
};

// Interface for updating an article (includes internal fields)
export type UpdateArticleDTO = Partial<CreateArticleDTO> & {
  slug?: string;
  reading_time?: number;
};

class ArticleService {
  
  async getAll(): Promise<IArticle[]> {
    const { data, error } = await publicSupabase
        .from('articles_with_authors')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });


    if (error) throw error;
    return data as IArticle[];
  }

  async getById(id: string): Promise<IArticle> {
    const { data, error } = await publicSupabase
        .from('articles_with_authors')
        .select('*')
        .eq('id', parseInt(id))
        .eq('status', 'published')
        .single();

    if (error) throw error;

    // Increment view count
    void this.incrementViews(parseInt(id));

    return data as IArticle;
  }

  async getBySlug(slug: string): Promise<IArticle> {
    const { data, error } = await publicSupabase
        .from('articles_with_authors')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

    if (error) throw error;

    // Increment view count
    void this.incrementViews(data.id);

    return data as IArticle;
  }

  async getByCategory(category: string): Promise<IArticle[]>{
    const { data, error } = await publicSupabase
        .from("articles_with_authors")
        .select('*')
        .eq('category', category)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data as IArticle[];
  }

  async getByAuthor(authorId: string): Promise<IArticle[]> {
    const { data, error } = await publicSupabase
        .from('articles_with_authors')
        .select('*')
        .eq('author_id', authorId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data as IArticle[];
  }

  async getUserArticles(): Promise<IArticle[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
        .from('articles_with_authors')
        .select('*')
        .eq('author_id', user.id)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data as IArticle[];
  }

  async createArticle(article: CreateArticleDTO): Promise<IArticle> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Generate slug from title
    const slug = this.generateSlug(article.title);
    
    // Calculate reading time
    const readingTime = this.calculateReadingTime(article.content);

    const { data, error } = await supabase
        .from('articles')
        .insert({
          ...article,
          author_id: user.id,
          slug,
          reading_time: readingTime,
          status: article.status || 'draft'
        })
        .select()
        .single();

    if (error) throw error;
    return data;
  }

  async updateArticle(id: number, updates: UpdateArticleDTO): Promise<IArticle> {
    // Update slug if title changed
    if (updates.title) {
      updates.slug = this.generateSlug(updates.title);
    }

    // Update reading time if content changed
    if (updates.content) {
      updates.reading_time = this.calculateReadingTime(updates.content);
    }

    const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

    if (error) throw error;
    return data;
  }

  async deleteArticle(id: number): Promise<void> {
    const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

    if (error) throw error;
  }

  private async incrementViews(id: number): Promise<void> {
    const { error } = await supabase.rpc('increment_views', { article_id_in: id });
    if (error) console.error('Error incrementing views:', error);
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  }

  private calculateReadingTime(content: string): number {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  }
}

export const articleService = new ArticleService();
